import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Spinner, Alert } from 'react-bootstrap';
import messagingApi from '../../services/messagingApi';
import { formatDateTime } from '../../utils/dateFormatter';

const SettingsTab = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  const fetchSchedulerStatus = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await messagingApi.getSchedulerStatus();
      setSchedulerStatus(data);
    } catch (err) {
      console.error('Error fetching scheduler status:', err);
      setError('فشل في تحميل حالة النظام');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedulerStatus();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchSchedulerStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  if (loading && !schedulerStatus) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">جاري التحميل...</p>
      </div>
    );
  }

  return (
    <div dir="rtl">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Row>
        {/* Scheduler Status Card */}
        <Col md={6} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <i className="bi bi-clock-history me-2" style={{ fontSize: '1.5rem', color: '#471396' }}></i>
                <h5 className="mb-0">حالة المجدول</h5>
              </div>

              {schedulerStatus?.scheduler && (
                <>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted">الحالة:</span>
                      <Badge bg={schedulerStatus.scheduler.enabled ? 'success' : 'secondary'}>
                        {schedulerStatus.scheduler.enabled ? 'نشط' : 'غير نشط'}
                      </Badge>
                    </div>

                    {schedulerStatus.scheduler.lastRun && (
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted">آخر تشغيل:</span>
                        <span>{formatDateTime(schedulerStatus.scheduler.lastRun)}</span>
                      </div>
                    )}

                    {schedulerStatus.scheduler.nextRun && (
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted">التشغيل القادم:</span>
                        <span>{formatDateTime(schedulerStatus.scheduler.nextRun)}</span>
                      </div>
                    )}

                    {schedulerStatus.scheduler.interval && (
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">الفاصل الزمني:</span>
                        <span>{schedulerStatus.scheduler.interval}</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Unifonic Configuration Card */}
        <Col md={6} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <i className="bi bi-gear me-2" style={{ fontSize: '1.5rem', color: '#471396' }}></i>
                <h5 className="mb-0">إعدادات Unifonic</h5>
              </div>

              {schedulerStatus?.unifonic && (
                <>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted">الحالة:</span>
                      <Badge bg={schedulerStatus.unifonic.configured ? 'success' : 'warning'}>
                        {schedulerStatus.unifonic.configured ? 'مُعد' : 'غير مُعد'}
                      </Badge>
                    </div>

                    {schedulerStatus.unifonic.appSid && (
                      <div className="mb-2">
                        <span className="text-muted">App SID:</span>
                        <div className="font-monospace small text-break">
                          {schedulerStatus.unifonic.appSid}
                        </div>
                      </div>
                    )}

                    {schedulerStatus.unifonic.senderId && (
                      <div className="mb-2">
                        <span className="text-muted">Sender ID:</span>
                        <div className="font-monospace small">
                          {schedulerStatus.unifonic.senderId}
                        </div>
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">وضع التجريب:</span>
                      <Badge bg={schedulerStatus.unifonic.mockMode ? 'info' : 'secondary'}>
                        {schedulerStatus.unifonic.mockMode ? 'نشط' : 'غير نشط'}
                      </Badge>
                    </div>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* System Stats Card */}
        <Col md={12} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <i className="bi bi-bar-chart me-2" style={{ fontSize: '1.5rem', color: '#471396' }}></i>
                <h5 className="mb-0">إحصائيات النظام</h5>
              </div>

              {schedulerStatus?.stats && (
                <Row>
                  <Col md={3} className="mb-3">
                    <div className="text-center p-3 bg-light rounded">
                      <div className="h3 mb-1 text-primary">
                        {schedulerStatus.stats.totalTemplates || 0}
                      </div>
                      <small className="text-muted">إجمالي القوالب</small>
                    </div>
                  </Col>

                  <Col md={3} className="mb-3">
                    <div className="text-center p-3 bg-light rounded">
                      <div className="h3 mb-1 text-success">
                        {schedulerStatus.stats.activeTemplates || 0}
                      </div>
                      <small className="text-muted">القوالب النشطة</small>
                    </div>
                  </Col>

                  <Col md={3} className="mb-3">
                    <div className="text-center p-3 bg-light rounded">
                      <div className="h3 mb-1 text-warning">
                        {schedulerStatus.stats.pendingMessages || 0}
                      </div>
                      <small className="text-muted">رسائل قيد الانتظار</small>
                    </div>
                  </Col>

                  <Col md={3} className="mb-3">
                    <div className="text-center p-3 bg-light rounded">
                      <div className="h3 mb-1 text-info">
                        {schedulerStatus.stats.todayMessages || 0}
                      </div>
                      <small className="text-muted">رسائل اليوم</small>
                    </div>
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* System Info */}
      <div className="text-center text-muted small mt-4">
        <p className="mb-1">نظام أتمتة الرسائل - Host Me</p>
        <p className="mb-0">
          آخر تحديث: {formatDateTime(new Date())}
        </p>
      </div>
    </div>
  );
};

export default SettingsTab;
